import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UpdateCardDialogComponent } from '@app/shared/components/update-card-dialog/update-card-dialog.component';
import { SubscriptionStateService } from '@app/shared/services/subscription-state.service';
import { StripePaymentService } from '@app/modules/account/service/stripe.service';
import { LAST_PAYMENT_STATUS_LABELS, LastPayment } from '@app/shared/models/last-payment.model';
import { SavedCard } from '@app/shared/models/subscription-renewal.model';
import {
  canChangeAutoRenew,
  findPlanPrice,
  PlanPrice,
  SUBSCRIPTION_DATE_FORMAT,
  SubscriptionInfo
} from '@app/shared/models/subscription-info.model';

type LoadState = 'loading' | 'loaded' | 'empty' | 'error';

/** Content of the profile's Subscription & Payment tab: plan, renewal, Auto-Renewal, last payment and saved card. */
@Component({
  selector: 'app-subscription-payment-tab',
  templateUrl: './subscription-payment-tab.component.html',
  styleUrls: ['./subscription-payment-tab.component.scss']
})
export class SubscriptionPaymentTabComponent implements OnInit {
  @Input() subscription: SubscriptionInfo | null = null;
  /** Customer-facing plan name, for example Premium Plan. */
  @Input() planLabel: string | null = null;
  /** Plans from the subscription-plans endpoint; the subscriber's price is matched by plan id. */
  @Input() plans: PlanPrice[] | null = null;

  readonly dateFormat = SUBSCRIPTION_DATE_FORMAT;
  readonly paymentLabels = LAST_PAYMENT_STATUS_LABELS;

  card: SavedCard | null = null;
  cardState: LoadState = 'loading';
  lastPayment: LastPayment | null = null;
  paymentState: LoadState = 'loading';

  @Input() userType: number = parseInt(localStorage.getItem('userType'), 10);

  constructor(
    private stripeService: StripePaymentService,
    private dialog: MatDialog,
    private subscriptionState: SubscriptionStateService
  ) { }

  /** Only the account owner may replace the card. */
  get canUpdateCard(): boolean {
    return canChangeAutoRenew(this.userType);
  }

  /** Opens the Update Card dialog. A replaced card is shown straight away from the dialog result, so the box does not flicker. */
  onUpdateCard(): void {
    this.dialog
      .open(UpdateCardDialogComponent, { width: '480px', height: 'auto', disableClose: true })
      .afterClosed()
      .subscribe((updated) => {
        if (updated) {
          this.card = updated;
          this.cardState = 'loaded';
          this.subscriptionState.notifySavedCardUpdated(updated);
        }
      });
  }

  ngOnInit(): void {
    this.loadCard();
    this.loadLastPayment();
  }

  get statusLabel(): string {
    switch (this.subscription && this.subscription.status) {
      case 'expired':
        return 'Expired';
      case 'expiringSoon':
        return 'Expiring Soon';
      default:
        return 'Active';
    }
  }

  /** Price of the subscriber's plan, null when it cannot be matched (the row is then hidden). */
  get renewalPrice(): PlanPrice | null {
    return findPlanPrice(this.plans, this.subscription && this.subscription.planId);
  }

  get renewalInterval(): string {
    const interval = this.renewalPrice && this.renewalPrice.interval;
    if (interval === 'year') {
      return 'year';
    }
    return interval === 'month' ? 'month' : '';
  }

  get hasExpiryDate(): boolean {
    return !!this.subscription && !!this.subscription.expiryDate && !isNaN(new Date(this.subscription.expiryDate).getTime());
  }

  /** Brand name and the last four digits only, for example "Visa •••• 4242". */
  get cardNumberText(): string {
    return this.card ? `${this.card.brand} •••• ${this.card.last4}` : '';
  }

  get cardExpiryText(): string {
    if (!this.card) {
      return '';
    }
    const month = String(this.card.expMonth).padStart(2, '0');
    return `${month}/${String(this.card.expYear).slice(-2)}`;
  }

  /** Reloads the saved card, for example after it was replaced. */
  loadCard(): void {
    this.cardState = 'loading';
    this.stripeService.getSavedCard(localStorage.getItem('userToken')).subscribe(
      (res) => {
        if (res && Number(res.statusCode) === 200 && res.data) {
          this.card = res.data;
          this.cardState = 'loaded';
        } else if (res && Number(res.statusCode) === 404) {
          this.card = null;
          this.cardState = 'empty';
        } else {
          this.cardState = 'error';
        }
      },
      () => (this.cardState = 'error')
    );
  }

  private loadLastPayment(): void {
    this.paymentState = 'loading';
    this.stripeService.getLastPayment(localStorage.getItem('userToken')).subscribe(
      (res) => {
        if (res && Number(res.statusCode) === 200) {
          this.lastPayment = res.data || null;
          this.paymentState = this.lastPayment ? 'loaded' : 'empty';
        } else {
          this.paymentState = 'error';
        }
      },
      () => (this.paymentState = 'error')
    );
  }
}
